<template>
  <DropdownMenu>
    <DropdownMenuTrigger>
      <slot />
    </DropdownMenuTrigger>
    <DropdownMenuContent
      align="start"
      class="turn-into-menu w-56 max-h-80 overflow-y-auto"
    >
      <DropdownMenuLabel class="turn-into-label"> Тип блока </DropdownMenuLabel>
      <DropdownMenuItem
        v-for="item in blockTypes"
        :key="item.label"
        :class="
          item.isActive?.()
            ? 'text-foreground font-medium'
            : 'text-muted-foreground'
        "
        @click="item.action"
      >
        <div class="turn-into-item-content">
          <span
            v-if="item.iconKey"
            class="turn-into-icon"
            v-html="getBlockIcon(item.iconKey)"
          />
          <span v-else-if="item.textIcon" class="turn-into-icon">
            {{ item.textIcon }}
          </span>
          <span class="turn-into-item-label">{{ item.label }}</span>
        </div>
      </DropdownMenuItem>

      <DropdownMenuSeparator />

      <DropdownMenuLabel class="turn-into-label"> Вставка </DropdownMenuLabel>
      <DropdownMenuItem
        v-for="item in insertItems"
        :key="item.label"
        class="text-muted-foreground"
        @click="item.action"
      >
        <div class="turn-into-item-content">
          <svg-icon
            :name="item.iconName"
            class="turn-into-icon fill-none text-muted-foreground"
          />
          <span class="turn-into-item-label">{{ item.label }}</span>
        </div>
      </DropdownMenuItem>

      <DropdownMenuSeparator />

      <DropdownMenuLabel class="turn-into-label"> Отступ </DropdownMenuLabel>
      <DropdownMenuItem
        v-for="item in indentItems"
        :key="item.label"
        class="text-muted-foreground"
        @click="item.action"
      >
        <div class="turn-into-item-content">
          <svg-icon
            :name="item.iconName"
            class="turn-into-icon fill-none text-muted-foreground"
          />
          <span class="turn-into-item-label">{{ item.label }}</span>
        </div>
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
</template>

<script setup>
import { computed } from "vue";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/shared/ui/dropdown-menu";
import { getBlockIcon } from "@/shared/ui/tiptap/blockIcons";

const props = defineProps({
  editor: {
    type: Object,
    required: true,
  },
  defaultParticipantCode: {
    type: String,
    default: null,
  },
});

const blockTypes = computed(() => [
  {
    label: "Параграф",
    iconKey: "paragraph",
    isActive: () =>
      props.editor.isActive("paragraph") && !props.editor.isActive("heading"),
    action: () => props.editor.chain().focus().setParagraph().run(),
  },
  {
    label: "Заголовок 1",
    iconKey: "h1",
    isActive: () => props.editor.isActive("heading", { level: 1 }),
    action: () =>
      props.editor.chain().focus().toggleHeading({ level: 1 }).run(),
  },
  {
    label: "Заголовок 2",
    iconKey: "h2",
    isActive: () => props.editor.isActive("heading", { level: 2 }),
    action: () =>
      props.editor.chain().focus().toggleHeading({ level: 2 }).run(),
  },
  {
    label: "Заголовок 3",
    iconKey: "h3",
    isActive: () => props.editor.isActive("heading", { level: 3 }),
    action: () =>
      props.editor.chain().focus().toggleHeading({ level: 3 }).run(),
  },
  {
    label: "Маркированный список",
    iconKey: "bullet-list",
    isActive: () => props.editor.isActive("bulletList"),
    action: () => props.editor.chain().focus().toggleBulletList().run(),
  },
  {
    label: "Нумерованный список",
    iconKey: "ordered-list",
    isActive: () => props.editor.isActive("orderedList"),
    action: () => props.editor.chain().focus().toggleOrderedList().run(),
  },
  {
    label: "Цитата",
    iconKey: "quote",
    isActive: () => props.editor.isActive("blockquote"),
    action: () => props.editor.chain().focus().toggleBlockquote().run(),
  },
  {
    label: "Блок кода",
    iconKey: "code",
    isActive: () => props.editor.isActive("codeBlock"),
    action: () => props.editor.chain().focus().toggleCodeBlock().run(),
  },
  ...(props.defaultParticipantCode
    ? [
        {
          label: "Повторяемая секция",
          textIcon: "↻",
          isActive: () => props.editor.isActive("repeatSection"),
          action: () =>
            props.editor.commands.wrapInRepeatSection({
              placeholderCode: props.defaultParticipantCode,
              label: props.defaultParticipantCode,
            }),
        },
      ]
    : []),
]);

const insertItems = computed(() => [
  {
    label: "Горизонтальная линия",
    iconName: "tt-horizontal-rule",
    action: () => props.editor.chain().focus().setHorizontalRule().run(),
  },
  {
    label: "Разрыв строки",
    iconName: "tt-hard-break",
    action: () => props.editor.chain().focus().setHardBreak().run(),
  },
]);

const indentItems = computed(() => [
  {
    label: "Увеличить отступ",
    iconName: "tt-align-right",
    action: () => props.editor.chain().focus().increaseIndent().run(),
  },
  {
    label: "Уменьшить отступ",
    iconName: "tt-align-left",
    action: () => props.editor.chain().focus().decreaseIndent().run(),
  },
]);
</script>

<style>
.turn-into-menu {
  min-width: 200px;
  border-radius: var(--radius-xl);
  padding: 0.5em;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04);
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    sans-serif;
}

.turn-into-menu .turn-into-label {
  padding: 0.5em 0.625em;
  font-size: 13px;
  font-weight: 700;
  color: hsl(var(--foreground));
}

.turn-into-menu [data-slot="dropdown-menu-item"] {
  padding: 0.5em;
  border-radius: 6px;
}

.turn-into-item-content {
  display: flex;
  align-items: center;
  gap: 0.625em;
}

.turn-into-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5em;
  height: 1.5em;
  flex-shrink: 0;
  font-size: 12px;
}

.turn-into-item-label {
  font-size: 15px;
  line-height: 1.3;
  white-space: nowrap;
}
</style>
